import { useCallback, useEffect, useState } from 'react'
import { Plus, ChevronDown, ChevronRight, Trash2, Loader2, Pencil, Check, X } from 'lucide-react'
import { useTeamsDataApi } from './useTeamsDataApi'

// Org list + per-org source URLs (catalog only in Phase 1; scraped in Phase 2+).
// Editor uses this surface to register PIHL / CHMA / CHE / ACHA / etc. and
// document where their roster CSVs come from before uploading.
export default function OrgsManager() {
  const api = useTeamsDataApi()
  const [orgs, setOrgs] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [expanded, setExpanded] = useState(new Set())
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ org_id: '', sport: 'hockey', display_name: '', short_name: '', level: '' })

  const refresh = useCallback(() => {
    setLoading(true); setErr(null)
    api.listOrgs()
      .then(r => setOrgs(r?.orgs || []))
      .catch(e => setErr(e?.message || String(e)))
      .finally(() => setLoading(false))
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  async function createOrg() {
    if (!draft.org_id || !draft.sport || !draft.display_name) {
      alert('org_id, sport, display_name required')
      return
    }
    try {
      await api.createOrg(draft)
      setDraft({ org_id: '', sport: 'hockey', display_name: '', short_name: '', level: '' })
      setCreating(false)
      refresh()
    } catch (e) {
      alert(`Failed: ${e?.message || e}`)
    }
  }

  function toggle(orgId) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(orgId)) next.delete(orgId); else next.add(orgId)
      return next
    })
  }

  if (loading) return <Loading />
  if (err) return <div style={{ color: '#f87171' }}>{err}</div>

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>{orgs.length} org{orgs.length === 1 ? '' : 's'}</h3>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(v => !v)}
          style={btnPrimary}>
          <Plus size={12} /> Add org
        </button>
      </div>

      {creating && (
        <div style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="org_id (lowercase, no spaces)">
              <input value={draft.org_id} onChange={e => setDraft({ ...draft, org_id: e.target.value })}
                placeholder="pihl" style={inp} />
            </Field>
            <Field label="sport">
              <input value={draft.sport} onChange={e => setDraft({ ...draft, sport: e.target.value })}
                placeholder="hockey" style={inp} />
            </Field>
            <Field label="display_name">
              <input value={draft.display_name} onChange={e => setDraft({ ...draft, display_name: e.target.value })}
                placeholder="Pittsburgh Interscholastic Hockey League" style={inp} />
            </Field>
            <Field label="short_name">
              <input value={draft.short_name} onChange={e => setDraft({ ...draft, short_name: e.target.value })}
                placeholder="PIHL" style={inp} />
            </Field>
            <Field label="level">
              <input value={draft.level} onChange={e => setDraft({ ...draft, level: e.target.value })}
                placeholder="high_school | college_d1 | amateur" style={inp} />
            </Field>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button onClick={createOrg} style={btnPrimary}>Create</button>
            <button onClick={() => setCreating(false)} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {orgs.map(o => (
          <OrgRow key={o.org_id} org={o}
            expanded={expanded.has(o.org_id)}
            onToggle={() => toggle(o.org_id)}
            onChanged={refresh}
            api={api} />
        ))}
        {orgs.length === 0 && (
          <div style={{ color: '#888', fontSize: 13 }}>
            No orgs registered yet. Click "Add org" to start — typical first row is PIHL (sport=hockey).
          </div>
        )}
      </div>
    </div>
  )
}

// Single org row. Shows summary inline; expanded view includes the
// source-URL editor + an "Edit metadata" toggle that flips to inline
// inputs for display_name / short_name / level / sport / notes.
function OrgRow({ org, expanded, onToggle, onChanged, api }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({
    display_name: org.display_name || '',
    short_name:   org.short_name   || '',
    level:        org.level        || '',
    sport:        org.sport        || '',
    notes:        org.notes        || '',
  })
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true)
    try {
      await api.updateOrg(org.org_id, {
        display_name: draft.display_name,
        short_name:   draft.short_name || null,
        level:        draft.level      || null,
        sport:        draft.sport,
        notes:        draft.notes      || null,
      })
      setEditing(false)
      onChanged()
    } catch (e) {
      alert(`Update failed: ${e?.message || e}`)
    } finally {
      setSaving(false)
    }
  }

  function cancel() {
    setDraft({
      display_name: org.display_name || '',
      short_name:   org.short_name   || '',
      level:        org.level        || '',
      sport:        org.sport        || '',
      notes:        org.notes        || '',
    })
    setEditing(false)
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ cursor: 'pointer' }} onClick={onToggle}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <strong style={{ color: '#dbe5f5' }}>{org.short_name || org.org_id}</strong>
        <span style={{ color: '#888', fontSize: 12 }}>· {org.display_name}</span>
        <span style={{ color: '#7dd3fc', fontSize: 11, padding: '1px 6px', background: 'rgba(125,211,252,0.1)', borderRadius: 10 }}>{org.sport}</span>
        {org.level && <span style={{ color: '#a78bfa', fontSize: 11, padding: '1px 6px', background: 'rgba(167,139,250,0.1)', borderRadius: 10 }}>{org.level}</span>}
        <div style={{ flex: 1 }} />
        <span style={{ color: '#888', fontSize: 11 }}>{org.team_count} teams · {org.player_count} players</span>
        <button onClick={() => setEditing(v => !v)}
          style={btnSecondaryXs} title={editing ? 'Close edit panel' : 'Edit org metadata'}>
          {editing ? <X size={11} /> : <Pencil size={11} />} {editing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      {editing && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #2a2a2a' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="display_name">
              <input value={draft.display_name} onChange={e => setDraft({ ...draft, display_name: e.target.value })}
                placeholder="College Hockey East" style={inp} />
            </Field>
            <Field label="short_name">
              <input value={draft.short_name} onChange={e => setDraft({ ...draft, short_name: e.target.value })}
                placeholder="CHE" style={inp} />
            </Field>
            <Field label="sport">
              <input value={draft.sport} onChange={e => setDraft({ ...draft, sport: e.target.value })}
                placeholder="hockey" style={inp} />
            </Field>
            <Field label="level">
              <input value={draft.level} onChange={e => setDraft({ ...draft, level: e.target.value })}
                placeholder="high_school | college_d1 | college_d3 | amateur" style={inp} />
            </Field>
            <Field label="notes">
              <input value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })}
                placeholder="optional editorial notes" style={inp} />
            </Field>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
              {saving ? <Loader2 size={11} className="spin" /> : <Check size={11} />} Save
            </button>
            <button onClick={cancel} disabled={saving} style={btnSecondary}>Cancel</button>
          </div>
          <div style={{ marginTop: 6, fontSize: 10, color: '#888' }}>
            org_id <code>{org.org_id}</code> cannot be renamed — it's the immutable key that ties uploads + roster rows together.
          </div>
        </div>
      )}

      {expanded && <OrgSourcesPanel orgId={org.org_id} api={api} />}
    </div>
  )
}

function OrgSourcesPanel({ orgId, api }) {
  const [sources, setSources] = useState(null)
  const [draft, setDraft] = useState({ url: '', kind: 'roster_index', description: '' })
  const [creating, setCreating] = useState(false)

  const refresh = useCallback(() => {
    api.listOrgSources(orgId).then(r => setSources(r?.sources || [])).catch(() => setSources([]))
  }, [api, orgId])

  useEffect(() => { refresh() }, [refresh])

  async function addSource() {
    if (!draft.url) return alert('url required')
    try {
      await api.createOrgSource(orgId, draft)
      setDraft({ url: '', kind: 'roster_index', description: '' })
      setCreating(false)
      refresh()
    } catch (e) { alert(`Failed: ${e?.message || e}`) }
  }

  async function deleteSource(source_id) {
    if (!confirm('Delete this source URL?')) return
    try { await api.deleteOrgSource(source_id); refresh() }
    catch (e) { alert(`Failed: ${e?.message || e}`) }
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #2a2a2a' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Source URLs</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(v => !v)} style={btnSecondaryXs}>
          <Plus size={10} /> add URL
        </button>
      </div>
      {creating && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr auto', gap: 6, marginBottom: 6 }}>
          <input placeholder="https://..." value={draft.url} onChange={e => setDraft({ ...draft, url: e.target.value })} style={inpXs} />
          <select value={draft.kind} onChange={e => setDraft({ ...draft, kind: e.target.value })} style={inpXs}>
            <option value="roster_index">roster_index</option>
            <option value="team_page">team_page</option>
            <option value="stats_module">stats_module</option>
            <option value="sportsengine_api">sportsengine_api</option>
            <option value="manual">manual</option>
          </select>
          <input placeholder="description" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} style={inpXs} />
          <button onClick={addSource} style={btnPrimaryXs}>Save</button>
        </div>
      )}
      {sources === null && <Loading small />}
      {sources && sources.length === 0 && (
        <div style={{ color: '#888', fontSize: 11, fontStyle: 'italic' }}>No source URLs cataloged.</div>
      )}
      {sources && sources.map(s => (
        <div key={s.source_id} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
          fontSize: 11, borderBottom: '1px dashed #1f2937',
        }}>
          <span style={{ color: '#7dd3fc', minWidth: 110, fontFamily: 'monospace' }}>{s.kind}</span>
          <a href={s.url} target="_blank" rel="noopener noreferrer"
            style={{ color: '#cbd5e1', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={s.url}>{s.url}</a>
          {s.description && <span style={{ color: '#888' }} title={s.description}>· {s.description.slice(0, 40)}</span>}
          <button onClick={() => deleteSource(s.source_id)}
            style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer' }}
            title="Delete">
            <Trash2 size={11} />
          </button>
        </div>
      ))}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#94a3b8' }}>
      {label}{children}
    </label>
  )
}

function Loading({ small = false }) {
  return <div style={{ color: '#888', fontSize: small ? 11 : 13 }}><Loader2 size={small ? 11 : 14} className="spin" style={{ display: 'inline', marginRight: 4 }} /> Loading…</div>
}

const card = { background: '#0d1622', border: '1px solid #1e293b', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }
const inp  = { padding: '6px 8px', background: '#020617', border: '1px solid #334155', color: '#dbe5f5', borderRadius: 3, fontSize: 12 }
const inpXs= { padding: '4px 6px', background: '#020617', border: '1px solid #334155', color: '#dbe5f5', borderRadius: 3, fontSize: 11 }
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#2563eb', border: 'none', color: '#fff', borderRadius: 4, fontSize: 12, cursor: 'pointer' }
const btnPrimaryXs = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', background: '#2563eb', border: 'none', color: '#fff', borderRadius: 3, fontSize: 11, cursor: 'pointer' }
const btnSecondary = { padding: '6px 12px', background: 'transparent', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 4, fontSize: 12, cursor: 'pointer' }
const btnSecondaryXs = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', background: 'transparent', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 3, fontSize: 11, cursor: 'pointer' }
